import React from "react";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import {
  alpha,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";

// Icons
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const NotFoundPage = () => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
    },
    tap: {
      scale: 0.95,
    },
  };

  // SVG animation variants
  const svgVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 1,
        when: "beforeChildren",
        staggerChildren: 0.2,
      },
    },
  };

  const pathVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        duration: 1.5,
        ease: "easeInOut",
      },
    },
  };

  const circleVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeInOut",
      },
    },
  };

  const handleGoHome = () => {
    history.push("/central-delivery");
  };

  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: alpha(theme.palette.background.default, 0.98),
        py: 6,
      }}
    >
      <Helmet>
        <title>Página Não Encontrada | Slapie</title>
      </Helmet>

      <Container maxWidth="md">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: 4,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Background decoration */}
            <Box
              sx={{
                position: "absolute",
                right: -100,
                top: -100,
                width: 300,
                height: 300,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(
                  theme.palette.primary.light,
                  0.1
                )} 0%, rgba(255,255,255,0) 70%)`,
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                left: -80,
                bottom: -80,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(
                  theme.palette.secondary.light,
                  0.1
                )} 0%, rgba(255,255,255,0) 70%)`,
                zIndex: 0,
              }}
            />

            <Grid
              container
              spacing={4}
              sx={{ position: "relative", zIndex: 1 }}
            >
              {/* Left side - 404 animation */}
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <motion.div
                  variants={svgVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Box
                    sx={{
                      width: { xs: 200, sm: 240, md: 280 },
                      height: { xs: 200, sm: 240, md: 280 },
                    }}
                  >
                    <svg
                      viewBox="0 0 200 200"
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      height="100%"
                    >
                      {/* Background circle */}
                      <motion.circle
                        cx="100"
                        cy="100"
                        r="90"
                        fill={alpha(theme.palette.primary.main, 0.05)}
                        stroke={alpha(theme.palette.primary.main, 0.2)}
                        strokeWidth="1"
                        variants={circleVariants}
                      />

                      {/* 4 */}
                      <motion.path
                        d="M65,130 L65,70 L35,110 L85,110"
                        fill="transparent"
                        stroke={theme.palette.primary.main}
                        strokeWidth="5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        variants={pathVariants}
                      />

                      {/* 0 */}
                      <motion.circle
                        cx="115"
                        cy="100"
                        r="25"
                        fill="transparent"
                        stroke={theme.palette.primary.main}
                        strokeWidth="5"
                        variants={pathVariants}
                      />

                      {/* 4 */}
                      <motion.path
                        d="M165,130 L165,70 L135,110 L185,110"
                        fill="transparent"
                        stroke={theme.palette.primary.main}
                        strokeWidth="5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        variants={pathVariants}
                      />
                    </svg>
                  </Box>
                </motion.div>
              </Grid>

              {/* Right side - content */}
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    justifyContent: "center",
                  }}
                >
                  <motion.div variants={itemVariants}>
                    <Typography
                      variant="h1"
                      fontWeight={800}
                      sx={{
                        mb: 2,
                        color: theme.palette.primary.main,
                        fontSize: { xs: "2.5rem", md: "3.5rem" },
                      }}
                    >
                      Oops!
                    </Typography>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      sx={{
                        mb: 3,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Página não encontrada
                    </Typography>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 4,
                        color: theme.palette.text.secondary,
                        maxWidth: 500,
                      }}
                    >
                      A página que você está tentando acessar não existe ou foi
                      movida. Por favor, verifique a URL ou retorne para uma das
                      nossas páginas principais.
                    </Typography>
                  </motion.div>

                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 5 }}
                  >
                    <motion.div variants={itemVariants}>
                      <motion.div
                        whileHover="hover"
                        whileTap="tap"
                        variants={buttonVariants}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          startIcon={<HomeIcon />}
                          onClick={handleGoHome}
                          sx={{
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            boxShadow: theme.shadows[2],
                          }}
                        >
                          Ir para o Dashboard
                        </Button>
                      </motion.div>
                    </motion.div>

                    <motion.div variants={itemVariants}>
                      <motion.div
                        whileHover="hover"
                        whileTap="tap"
                        variants={buttonVariants}
                      >
                        <Button
                          variant="outlined"
                          color="primary"
                          size="large"
                          startIcon={<ArrowBackIcon />}
                          onClick={handleGoBack}
                          sx={{
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                          }}
                        >
                          Voltar
                        </Button>
                      </motion.div>
                    </motion.div>
                  </Box>

                  {/* Help section */}
                  <motion.div variants={itemVariants}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: alpha(
                          theme.palette.background.default,
                          0.5
                        ),
                        border: `1px solid ${alpha(
                          theme.palette.divider,
                          0.1
                        )}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        <HelpOutlineIcon
                          fontSize="small"
                          sx={{ mr: 1, color: theme.palette.info.main }}
                        />
                        Precisa de ajuda?
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Se você acha que isso é um erro, entre em contato com o
                        suporte ou tente buscar pelo que precisa usando a
                        pesquisa no menu.
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default NotFoundPage;
